import { Theme } from "@/scripts/theme";
import { defineStore } from "pinia";
import { useLocalStorage } from "@vueuse/core";
import { useAccountStore } from "@/model/account";
import Ajv, { JSONSchemaType } from "ajv";
import { legacyApi } from "@/api-client";

export const useThemeStore = defineStore("theme", {
    state: () => ({
        themes: useLocalStorage("mag/themes", [] as Theme[], {
            listenToStorageChanges: true,
            deep: true,
        }),
    }),
    actions: {
        async addTheme(theme: Theme) {
            this.themes = [
                ...this.themes.filter((t) => t.id !== theme.id),
                theme,
            ];

            const { current } = useAccountStore();
            if (!current) {
                return;
            }

            await legacyApi(
                "i/registry/set",
                {
                    scope: ["client"],
                    key: "themes",
                    value: this.themes,
                },
                current,
            );
        },
        async removeTheme(theme: Theme) {
            this.themes = this.themes.filter((t) => t.id !== theme.id);

            const { current } = useAccountStore();
            if (!current) {
                return;
            }

            await legacyApi(
                "i/registry/set",
                {
                    scope: ["client"],
                    key: "themes",
                    value: this.themes,
                },
                current,
            );
        },
        async fetchThemes() {
            const { current } = useAccountStore();
            if (!current) {
                return;
            }

            try {
                const themes = await legacyApi(
                    "i/registry/get",
                    {
                        scope: ["client"],
                        key: "themes",
                    },
                    current,
                );

                const ajv = new Ajv();
                const validate = ajv.compile<Theme[]>({
                    type: "array",
                    items: themeSchema,
                });

                if (!validate(themes)) {
                    throw "Failed to validate user themes";
                }

                const fetchedIds = new Set(themes.map((t) => t.id));

                this.themes = {
                    ...this.themes.filter((t) => !fetchedIds.has(t.id)),
                    ...themes,
                };
            } catch (err) {
                if (err?.["code"] === "NO_SUCH_KEY") return;
                throw err;
            }
        },
    },
    getters: {
        hasThemeId() {
            return (id: string) => this.themes.some((t) => t.id === id);
        },
    },
});

export const themeSchema = {
    type: "object",
    properties: {
        id: { type: "string" },
        name: { type: "string" },
        author: { type: "string" },
        desc: { type: "string", nullable: true },
        base: { type: "string", enum: ["dark", "light"] },
        props: {
            type: "object",
            patternProperties: { "^X": { type: "string" } },
            required: [],
        },
    },
    required: ["id", "name", "author", "base", "props"],
    additionalProperties: false,
} satisfies JSONSchemaType<Theme>;

export const validateTheme = (theme: any): theme is Theme => {
    const ajv = new Ajv();
    const validate = ajv.compile(themeSchema);
    return validate(theme);
};
